import { Flex, Spinner } from "@chakra-ui/react";
import DashboardLayout from "components/DashboardLayout";
import { ROUTES } from "constants/routes";
import Dashboard from "pages/Dashboard";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { RedirectIfAuthed, RequireAuth } from "./RequireAuth";

const Login = lazy(() => import("pages/Login"));
const ForgotPassword = lazy(() => import("pages/ForgotPassword"));
const ResetPassword = lazy(() => import("pages/ResetPassword"));

const Users = lazy(() => import("pages/Users"));
const Transactions = lazy(() => import("pages/Transactions"));
const Charity = lazy(() => import("pages/Charity"));
const ExchangeRates = lazy(() => import("pages/ExchangeRates"));
const Beneficiaries = lazy(() => import("pages/Beneficiaries"));
const DepositTransactions = lazy(() => import("pages/DepositTransactions"));
const TransferTransactions = lazy(() => import("pages/TransferTransactions"));
const BambooTransfers = lazy(() => import("pages/BambooTransfers"));
const ViewDonors = lazy(() => import("pages/Charity/ViewDonors"));
const Sanctions = lazy(() => import("pages/Sanction"));
const Currency = lazy(() => import("pages/Currency"));
const Profile = lazy(() => import("pages/Profile"));
const AdminUsers = lazy(() => import("pages/AdminUsers"));
const FeatureFlags = lazy(() => import("pages/FeatureFlags"));
const Audit = lazy(() => import("pages/Audit"));
const MarketingOverview = lazy(() => import("pages/Marketing/Overview"));
const Partners = lazy(() => import("pages/Marketing/Partners"));
const OnboardPartner = lazy(() => import("pages/Marketing/Partners/OnboardPartner"));
const PartnerDetails = lazy(() => import("pages/Marketing/Partners/PartnerDetails"));
const Vouchers = lazy(() => import("pages/Marketing/Vouchers"));
const IssueVouchers = lazy(() => import("pages/Marketing/Vouchers/IssueVouchers"));
const BatchDetails = lazy(() => import("pages/Marketing/Vouchers/BatchDetails"));
const Redemptions = lazy(() => import("pages/Marketing/Redemptions"));

const RouteWrapper = (children: React.ReactNode | null) => (
  <Suspense
    fallback={
      <Flex h="100vh" w="100%" align="center" justify="center">
        <Spinner size="lg" color="primary" thickness="3px" />
      </Flex>
    }
  >
    {children}
  </Suspense>
);

const AppRouter = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path={ROUTES.MAIN.path}
          element={
            <RequireAuth>{RouteWrapper(<DashboardLayout />)}</RequireAuth>
          }
        >
          <Route
            path={ROUTES.DASHBOARD.path}
            element={RouteWrapper(<Dashboard />)}
          />
          <Route path={ROUTES.USERS.path} element={RouteWrapper(<Users />)} />
          <Route
            path={ROUTES.TRANSACTIONS.path}
            element={RouteWrapper(<Transactions />)}
          />
          <Route
            path={ROUTES.CHARITY.path}
            element={RouteWrapper(<Charity />)}
          />
          <Route
            path={ROUTES.DEPOSITS.path}
            element={RouteWrapper(<DepositTransactions />)}
          />
          <Route
            path={ROUTES.TRANSFERS.path}
            element={RouteWrapper(<TransferTransactions />)}
          />
          <Route
            path={ROUTES.BAMBOO_TRANSFERS.path}
            element={RouteWrapper(<BambooTransfers />)}
          />
          <Route
            path={ROUTES.VIEW_DONORS.path}
            element={RouteWrapper(<ViewDonors />)}
          />

          <Route
            path={ROUTES.PROFILE.path}
            element={RouteWrapper(<Profile />)}
          />

          <Route
            path={ROUTES.ADMIN_USERS.path}
            element={RouteWrapper(<AdminUsers />)}
          />

          <Route
            path={ROUTES.FEATURE_FLAGS.path}
            element={RouteWrapper(<FeatureFlags />)}
          />

          <Route
            path={ROUTES.AUDIT_TRAIL.path}
            element={RouteWrapper(<Audit />)}
          />

          <Route
            path={ROUTES.FLAGGED_USERS.path}
            element={RouteWrapper(<Sanctions />)}
          />
          <Route
            path={ROUTES.EXCHANGE_RATES.path}
            element={RouteWrapper(<ExchangeRates />)}
          />
          <Route
            path={ROUTES.BENEFICIARIES.path}
            element={RouteWrapper(<Beneficiaries />)}
          />
          <Route
            path={ROUTES.CURRENCIES.path}
            element={RouteWrapper(<Currency />)}
          />

          <Route
            path={ROUTES.MARKETING.path}
            element={<Navigate replace to={ROUTES.MARKETING_OVERVIEW.path} />}
          />
          <Route
            path={ROUTES.MARKETING_OVERVIEW.path}
            element={RouteWrapper(<MarketingOverview />)}
          />
          <Route
            path={ROUTES.PARTNERS.path}
            element={RouteWrapper(<Partners />)}
          />
          <Route
            path={ROUTES.ONBOARD_PARTNER.path}
            element={RouteWrapper(<OnboardPartner />)}
          />
          <Route
            path={ROUTES.PARTNER_DETAILS.path}
            element={RouteWrapper(<PartnerDetails />)}
          />
          <Route
            path={ROUTES.VOUCHERS.path}
            element={RouteWrapper(<Vouchers />)}
          />
          <Route
            path={ROUTES.ISSUE_VOUCHERS.path}
            element={RouteWrapper(<IssueVouchers />)}
          />
          <Route
            path={ROUTES.VOUCHER_BATCH_DETAILS.path}
            element={RouteWrapper(<BatchDetails />)}
          />
          <Route
            path={ROUTES.REDEMPTIONS.path}
            element={RouteWrapper(<Redemptions />)}
          />
        </Route>

        <Route path={ROUTES.AUTH.path}>
          <Route
            path={ROUTES.LOGIN.path}
            element={
              <RedirectIfAuthed>{RouteWrapper(<Login />)}</RedirectIfAuthed>
            }
          />

          <Route
            path={ROUTES.ENABLE_2FA.path}
            element={
              <RequireAuth><Navigate replace to={ROUTES.PROFILE.path} /></RequireAuth>
            }
          />

          <Route
            path={ROUTES.FORGOT_PASSWORD.path}
            element={RouteWrapper(<ForgotPassword />)}
          />

          <Route
            path={ROUTES.RESET_PASSWORD.path}
            element={RouteWrapper(<ResetPassword />)}
          />
        </Route>

        <Route
          path={ROUTES.CATCH_ALL.path}
          element={<Navigate replace to={ROUTES.LOGIN.path} />}
        />
      </Routes>
    </BrowserRouter>
  );
};
export default AppRouter;
